import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, ZoomIn, ZoomOut, Download, RotateCw } from "lucide-react";

/**
 * Full-screen chat image lightbox with pinch/scroll zoom, drag-to-pan, rotate, and download.
 * Opens on tap of any chat image (admin or user), works on both mobile and desktop.
 *
 * Props:
 *   open, src, alt, onClose
 */
export default function ImageLightbox({ open, src, alt = "", onClose }) {
    const [scale, setScale] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const dragRef = useRef(null); // { startX, startY, baseTx, baseTy, active }
    const pinchRef = useRef(null); // { startDist, startScale }

    // Reset transform when a new image opens
    useEffect(() => {
        if (open) {
            setScale(1);
            setRotation(0);
            setTx(0);
            setTy(0);
        }
    }, [open, src]);

    // ESC to close, +/- to zoom, R to rotate
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            else if (e.key === "+" || e.key === "=") setScale((s) => Math.min(6, s + 0.25));
            else if (e.key === "-" || e.key === "_") setScale((s) => Math.max(1, s - 0.25));
            else if (e.key === "r" || e.key === "R") setRotation((r) => (r + 90) % 360);
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    // Lock body scroll while open (belt + suspenders — chat page already locks it)
    useEffect(() => {
        if (!open) return undefined;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    if (!open) return null;

    const onWheel = (e) => {
        e.preventDefault();
        const dir = e.deltaY < 0 ? 1 : -1;
        setScale((s) => Math.max(1, Math.min(6, +(s + dir * 0.15).toFixed(2))));
    };

    const onDoubleClick = () => setScale((s) => (s > 1 ? 1 : 2.5));

    // Mouse drag pan
    const onMouseDown = (e) => {
        if (scale <= 1) return;
        dragRef.current = { startX: e.clientX, startY: e.clientY, baseTx: tx, baseTy: ty, active: true };
    };
    const onMouseMove = (e) => {
        const d = dragRef.current;
        if (!d?.active) return;
        setTx(d.baseTx + (e.clientX - d.startX));
        setTy(d.baseTy + (e.clientY - d.startY));
    };
    const onMouseUp = () => { if (dragRef.current) dragRef.current.active = false; };

    // Touch pinch + pan
    const distance = (t1, t2) => Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            pinchRef.current = { startDist: distance(e.touches[0], e.touches[1]), startScale: scale };
        } else if (e.touches.length === 1 && scale > 1) {
            const t = e.touches[0];
            dragRef.current = { startX: t.clientX, startY: t.clientY, baseTx: tx, baseTy: ty, active: true };
        }
    };
    const onTouchMove = (e) => {
        if (e.touches.length === 2 && pinchRef.current) {
            const d = distance(e.touches[0], e.touches[1]);
            const s = pinchRef.current.startScale * (d / pinchRef.current.startDist);
            setScale(Math.max(1, Math.min(6, s)));
        } else if (e.touches.length === 1 && dragRef.current?.active) {
            const t = e.touches[0];
            setTx(dragRef.current.baseTx + (t.clientX - dragRef.current.startX));
            setTy(dragRef.current.baseTy + (t.clientY - dragRef.current.startY));
        }
    };
    const onTouchEnd = () => {
        pinchRef.current = null;
        if (dragRef.current) dragRef.current.active = false;
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center touch-none select-none"
            data-testid="chat-image-lightbox"
            role="dialog"
            aria-modal="true"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            {/* Toolbar */}
            <div className="absolute top-3 right-3 flex items-center gap-2 z-10">
                <button
                    onClick={() => setScale((s) => Math.max(1, s - 0.5))}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur"
                    title="Zoom out"
                    data-testid="lightbox-zoom-out"
                ><ZoomOut className="w-5 h-5" /></button>
                <button
                    onClick={() => setScale((s) => Math.min(6, s + 0.5))}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur"
                    title="Zoom in"
                    data-testid="lightbox-zoom-in"
                ><ZoomIn className="w-5 h-5" /></button>
                <button
                    onClick={() => setRotation((r) => (r + 90) % 360)}
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur"
                    title="Rotate"
                    data-testid="lightbox-rotate"
                ><RotateCw className="w-5 h-5" /></button>
                <a
                    href={src}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                    className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center backdrop-blur"
                    title="Open original"
                    data-testid="lightbox-download"
                ><Download className="w-5 h-5" /></a>
                <button
                    onClick={onClose}
                    className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center backdrop-blur"
                    title="Close (Esc)"
                    data-testid="lightbox-close"
                ><X className="w-5 h-5" /></button>
            </div>

            {/* Scale badge */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-[11px] tracking-wider bg-white/10 px-3 py-1 rounded-full backdrop-blur">
                {Math.round(scale * 100)}% · Double-tap to zoom · Pinch on mobile
            </div>

            {/* The image */}
            <img
                src={src}
                alt={alt || "chat attachment"}
                data-testid="chat-image-lightbox-img"
                draggable={false}
                onDoubleClick={onDoubleClick}
                onWheel={onWheel}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={onMouseUp}
                onMouseLeave={onMouseUp}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                onClick={(e) => e.stopPropagation()}
                style={{
                    transform: `translate(${tx}px, ${ty}px) scale(${scale}) rotate(${rotation}deg)`,
                    transformOrigin: "center",
                    transition: dragRef.current?.active ? "none" : "transform 120ms ease",
                    cursor: scale > 1 ? "grab" : "zoom-in",
                    maxWidth: "92vw",
                    maxHeight: "88vh",
                }}
                className="object-contain rounded-md shadow-2xl"
            />
        </div>,
        document.body,
    );
}
